import { addressResponseType, errorFormatter } from '@utils';
import 'leaflet/dist/leaflet.css';
import { useState, FunctionComponent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { scrollTop } from '@utils';
import { RoutesList } from '../../../routes/Routes';
import { ErrorMessages } from '@types';
import { MapTemplate } from '@templates';
import { useWasteStore } from '@waste/store/store';
import {
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
} from '@waste/utils/formatServiceLocation';
import { LatLngTuple } from 'leaflet';
import { emptyLocation } from '@waste/store/storeSlices/wasteDetails';
import { locationValidation } from '@utils';
import { ZodError } from 'zod';

export const WasteLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const { locationOfWaste, updateLocationOfWaste } = useWasteStore();
  const initialAddress: addressResponseType = locationOfWaste.address_line_1
    ? convertServiceLocationToAddress(locationOfWaste)
    : ({} as addressResponseType);

  const getLatLongTuple = (
    address: addressResponseType,
  ): LatLngTuple | undefined => {
    if (address?.latitude && address?.longitude) {
      const lat = parseFloat(address?.latitude);
      const long = parseFloat(address?.longitude);
      return [lat, long];
    }
    return undefined;
  };

  const [address, setAddress] = useState<addressResponseType>(initialAddress);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [errorPaths, setErrorPaths] = useState<string>('');
  const [defaultLocation, setDefaultLocation] = useState<
    LatLngTuple | undefined
  >(getLatLongTuple(initialAddress));
  const labelText = `Where is the waste located?`;
  const handleOnNextClick = () => {
    try {
      locationValidation.parse({
        locationOfWaste: locationOfWaste.UPRN ?? '',
      });
      if (!address || !address.latitude || !address.longitude) {
        return;
      }
      updateLocationOfWaste(convertAddressToServiceLocation(address));

      navigate(RoutesList.ADDITIONAL_DETAILS);
    } catch (error) {
      const { errorPaths, errorMessages } = errorFormatter(error as ZodError);
      setErrorMessages(errorMessages);
      setErrorPaths(errorPaths);
    }
  };

  const buttonLeft = {
    label: 'Previous',
    onClick: () => {
      navigate(RoutesList.WASTE_DETAILS);
      scrollTop();
    },
  };
  const buttonRight = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = (address: addressResponseType) => {
    setAddress(address);
    const updatedServiceLocation = address.address_line_1
      ? convertAddressToServiceLocation(address)
      : emptyLocation;
    updateLocationOfWaste(updatedServiceLocation);
    setErrorMessages([]);
  };

  useEffect(() => {
    if (address) {
      const loc: LatLngTuple | undefined = getLatLongTuple(address);
      setDefaultLocation(loc);
    }
  }, [address]);

  return (
    <MapTemplate
      labelText={labelText}
      defaultLocation={defaultLocation}
      errorPath={errorPaths}
      errorMessages={errorMessages}
      onChangeAddress={handleAddressChange}
      selectedAddress={address}
      buttonNext={buttonRight}
      buttonPrevious={buttonLeft}
    />
  );
};
